import { createGetCustomerInput, getCustomerActionDataAction } from '@msdyn365-commerce/action-internal';
import { ICoreContext } from '@msdyn365-commerce/core';
import { IssueLoyaltyInput, issueLoyalty } from '@msdyn365-commerce-modules/retail-actions';
import { Customer, CustomersDataActions, LoyaltyCard, StoreOperationsDataActions } from '@msdyn365-commerce/retail-proxy';

export const PRIMARY_LOYALTY_CARD_PROPERTY = 'BS_PRIMARYLOYALTYCARDID';

export const getCustomerPrimaryLoyaltyCardId = (customer?: Customer | null): string | null =>
    customer?.ExtensionProperties?.find(property => property.Key === PRIMARY_LOYALTY_CARD_PROPERTY)?.Value?.StringValue ?? null;

const _getCurrentCustomer = async (context: ICoreContext, customer?: Customer | null): Promise<Customer | null> => {
    if (customer) {
        return customer;
    }

    const input = createGetCustomerInput(context.actionContext);
    return (await getCustomerActionDataAction(input, context.actionContext)) ?? null;
};

export const getFirstCustomerLoyaltyCardId = async (context: ICoreContext): Promise<string | null> => {
    const cards = await StoreOperationsDataActions.getCustomerLoyaltyCardsAsync(
        { callerContext: context.actionContext, queryResultSettings: {} },
        null
    );

    return cards?.[0]?.CardNumber ?? null;
};

export const setCustomerPrimaryLoyaltyCardId = async (context: ICoreContext, customer: Customer, loyaltyCardId: string): Promise<void> => {
    if (!loyaltyCardId || getCustomerPrimaryLoyaltyCardId(customer) === loyaltyCardId) {
        return;
    }

    customer.ExtensionProperties = customer.ExtensionProperties ?? [];

    const primaryLoyaltyProperty = customer.ExtensionProperties.find(property => property.Key === PRIMARY_LOYALTY_CARD_PROPERTY);
    if (!primaryLoyaltyProperty) {
        customer.ExtensionProperties.push({
            Key: PRIMARY_LOYALTY_CARD_PROPERTY,
            Value: { StringValue: loyaltyCardId }
        });
    } else {
        primaryLoyaltyProperty.Value = {
            ...(primaryLoyaltyProperty.Value ?? {}),
            StringValue: loyaltyCardId
        };
    }

    await CustomersDataActions.updateAsync({ callerContext: context.actionContext }, customer);
};

export const findCustomerLoyaltyCardId = async (context: ICoreContext, customer?: Customer | null): Promise<string | null> => {
    const currentCustomer = await _getCurrentCustomer(context, customer);
    const primaryLoyaltyCardId = getCustomerPrimaryLoyaltyCardId(currentCustomer);

    if (primaryLoyaltyCardId) {
        return primaryLoyaltyCardId;
    }

    return getFirstCustomerLoyaltyCardId(context);
};

export const issueLoyaltyCardForCustomer = async (context: ICoreContext, customer?: Customer | null): Promise<LoyaltyCard | null> => {
    if (!context.request.user?.isAuthenticated) {
        return null;
    }

    const currentCustomer = await _getCurrentCustomer(context, customer);
    if (!currentCustomer) {
        return null;
    }

    const input = new IssueLoyaltyInput(context.request.apiSettings);
    const loyaltyCard = await issueLoyalty(input, context.actionContext);

    if (loyaltyCard?.CardNumber) {
        await setCustomerPrimaryLoyaltyCardId(context, currentCustomer, loyaltyCard.CardNumber);
    }

    return loyaltyCard;
};

/**
 * Module-level singleton promise that serializes loyalty card issuance across concurrent callers
 * (e.g. header and checkout running in the same page at the same time).
 */
let _pendingIssuance: Promise<string | null> | null = null;

/**
 * Ensures the authenticated customer has a primary loyalty card.
 * - Returns an existing card number immediately if one is found.
 * - If no card exists, issues one and marks it as primary.
 * - Concurrent callers share a single issuance call via a module-level promise,
 *   so only one card is ever issued regardless of how many callers trigger this simultaneously.
 */
export const ensureCustomerPrimaryLoyaltyCard = async (context: ICoreContext, customer?: Customer | null): Promise<string | null> => {
    if (typeof window === 'undefined') {
        return null;
    }
    if (!context.request.user?.isAuthenticated) {
        return null;
    }

    const currentCustomer = await _getCurrentCustomer(context, customer);
    if (!currentCustomer) {
        return null;
    }

    if (
        currentCustomer.ExtensionProperties?.find(
            property => property.Key === 'BS_ISLOYALTYCARDCREATIONINPROGRESS' && property.Value?.BooleanValue
        )
    ) {
        return null;
    }

    const primaryCardId = getCustomerPrimaryLoyaltyCardId(currentCustomer);
    const existingCardId = primaryCardId ?? (await getFirstCustomerLoyaltyCardId(context));

    if (existingCardId) {
        if (!primaryCardId) {
            await setCustomerPrimaryLoyaltyCardId(context, currentCustomer, existingCardId);
        }

        return existingCardId;
    }

    // No card found — use a shared promise so concurrent callers issue exactly one card.
    if (!_pendingIssuance) {
        _pendingIssuance = (async () => {
            try {
                const loyaltyCard = await issueLoyaltyCardForCustomer(context, currentCustomer);
                const issuedCardId = loyaltyCard?.CardNumber ?? null;

                return issuedCardId;
            } finally {
                _pendingIssuance = null;
            }
        })();
    }

    return _pendingIssuance;
};
